/**
 * Time helpers. The demo is always "live": quarters, months and timestamps are
 * computed from the browser clock so the data never looks stale. Quarters are
 * Australian financial-year quarters labelled by the FY-end year, exactly like
 * the real backend: Oct–Dec 2026 is `2027-Q2`.
 */

/** First month of data, matching the real dashboard's data window (Dec 2025). */
export const DATA_START = new Date(2025, 11, 1);

export interface Quarter {
    key: string; // 2027-Q2
    fy: number; // 2027
    q: number; // 1..4
    start: Date; // first day, local
    end: Date; // last day, local, 23:59:59
    label: string; // Q2 FY27 (Oct–Dec)
    shortLabel: string; // Q2 2027
    months: string[]; // ['2026-10','2026-11','2026-12']
}

const RANGE = ['', 'Jul–Sep', 'Oct–Dec', 'Jan–Mar', 'Apr–Jun'];

export function now(): Date {
    return new Date();
}

export function ym(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function ymd(d: Date): string {
    return `${ym(d)}-${String(d.getDate()).padStart(2, '0')}`;
}

/** ISO 8601 with the Darwin offset (+09:30), as the backend emits. */
export function iso(d: Date): string {
    const local = new Date(d.getTime() + 9.5 * 3600 * 1000);
    return local.toISOString().replace(/\.\d{3}Z$/, '+09:30');
}

export function addDays(d: Date, days: number): Date {
    const r = new Date(d);
    r.setDate(r.getDate() + days);
    return r;
}

export function addMonths(d: Date, months: number): Date {
    return new Date(d.getFullYear(), d.getMonth() + months, 1);
}

export function quarterOf(d: Date): Quarter {
    const m = d.getMonth(); // 0..11
    const fy = m >= 6 ? d.getFullYear() + 1 : d.getFullYear();
    const q = Math.floor(((m + 6) % 12) / 3) + 1;
    return quarter(`${fy}-Q${q}`);
}

export function quarter(key: string): Quarter {
    const [fyStr, qStr] = key.split('-Q');
    const fy = Number(fyStr);
    const q = Number(qStr);
    const startMonth = (6 + (q - 1) * 3) % 12; // Jul=6
    const startYear = q <= 2 ? fy - 1 : fy;
    const start = new Date(startYear, startMonth, 1);
    const end = new Date(startYear, startMonth + 3, 0, 23, 59, 59);
    const months = [0, 1, 2].map(i => ym(new Date(startYear, startMonth + i, 1)));
    return {
        key,
        fy,
        q,
        start,
        end,
        label: `Q${q} FY${String(fy).slice(2)} (${RANGE[q]})`,
        shortLabel: `Q${q} ${fy}`,
        months,
    };
}

export function currentQuarter(): Quarter {
    return quarterOf(now());
}

/** Newest first, back to the quarter containing DATA_START. */
export function allQuarters(): Quarter[] {
    const out: Quarter[] = [];
    let q = currentQuarter();
    const first = quarterOf(DATA_START).key;
    for (let guard = 0; guard < 40; guard++) {
        out.push(q);
        if (q.key === first) {
            break;
        }
        q = quarterOf(addDays(q.start, -1));
    }
    return out;
}

/** The quarter named by `?quarter=`, or null for "All Time". */
export function quarterParam(query: URLSearchParams): Quarter | null {
    const v = query.get('quarter');
    return v && /^\d{4}-Q[1-4]$/.test(v) ? quarter(v) : null;
}

/** Months (YYYY-MM) from DATA_START to the current month inclusive. */
export function monthsToDate(): string[] {
    const out: string[] = [];
    for (let d = new Date(DATA_START); d <= now(); d = addMonths(d, 1)) {
        out.push(ym(d));
    }
    return out;
}

/** Months in the current financial year to date (Jul..now). */
export function fyMonthsToDate(): string[] {
    const fy = currentQuarter().fy;
    return monthsToDate().filter(m => quarterOf(new Date(`${m}-01T00:00:00`)).fy === fy);
}

/** Fraction of `q` that has elapsed (1 for past quarters). */
export function elapsed(q: Quarter): number {
    const n = now().getTime();
    if (n >= q.end.getTime()) {
        return 1;
    }
    if (n <= q.start.getTime()) {
        return 0;
    }
    return (n - q.start.getTime()) / (q.end.getTime() - q.start.getTime());
}

export function monthLabel(m: string): string {
    const d = new Date(`${m}-01T00:00:00`);
    return d.toLocaleString('en-AU', { month: 'short', year: 'numeric' });
}
